import angular from 'angular';
import dayjs from 'dayjs';
import Utils from '~/scripts/services/utils';

import APIV5 from '~/models/api';

import { buildV5Url } from '~/utils/v5-url';

import logo from '~/images/logo.png';

import templateModalLoginWorkspaces from './modal/modal-login-workspaces.html';
import ModalLoginWorkspacesController from './modal/modal-login-workspaces.controller';

import templateModalLoginConfirmResetPassword from './modal/modal-login-confirm-reset-password.html';
import ModalLoginConfirmResetPasswordController from './modal/modal-login-confirm-reset-password.controller';

import templateModalLoginPasswordResetMail from './modal/modal-login-password-reset-mail.html';
import ModalLoginPasswordResetMailController from './modal/modal-login-password-reset-mail.controller';

/** @type {"K_NODE;TERM_WEB" | "K_NODE;TERM_SMARTPHONE" | "K_NODE;TERM_AGENT"} */
const terminalid = 'K_NODE;TERM_WEB'

/**
 * Marge de fermeture anticipée de la modale de sélection, avant l'expiration réelle du
 * ticket de pré-authentification. Évite qu'une sélection faite à la dernière seconde
 * parte vers un ticket déjà périmé.
 */
const PRE_AUTH_CLOSE_MARGIN_MS = 5000;

/** Motif de rejet de la modale lorsque le ticket de pré-authentification a expiré. */
const PRE_AUTH_EXPIRED = 'auth-pending-expired';

/* globals Knitiv */

/**
 * @param {import('@knitiv/api-client-javascript').AuthenticationObject
 * 	 & ?{config: import('@knitiv/api-client-javascript').Config}} context
 * @param {string} url
 * @param {string} version
 */
const setAPI = (context, url, version) => {
	if (context?.url) {
		APIV5.instance.setBackEndpoint(context?.url.back ?? '/');
		APIV5.instance.setFileEndpoint(context?.url.file ?? '/');
	}
	else if (context?.config) {
		APIV5.instance.setBackEndpoint(context?.config.BACK?.url ?? '/');
		APIV5.instance.setFileEndpoint(context?.config.FILE?.url ?? '/');
	}
	APIV5.instance.setToken(context.token.id);
	APIV5.instance.setFrontURL(url);
	APIV5.instance.setFrontVersion(version);
};

export default class LoginCtrl {
	/**
	 * Mode de transmission de l'identité, tel que décidé par le serveur lors de
	 * `WorkspaceList`.
	 *
	 * La décision appartient au back — il pose ou non le cookie de pré-authentification
	 * selon la méthode d'authentification. Le front ne fait que constater ce qui a été
	 * fait, il n'arbitre pas.
	 *
	 * `legacy` sur toute autre valeur, y compris l'absence du champ, pour qu'un back ne
	 * l'exposant pas encore conserve le comportement actuel.
	 *
	 * @param {Partial<import('@knitiv/api-client-javascript').WorkspaceListOutput> | undefined} session
	 * 	 La réponse de `WorkspaceList`
	 * @returns {'pre_auth' | 'legacy'} Le mode retenu pour cette connexion
	 */
	static authMode(session) {
		return session?.auth_mode === 'pre_auth' ? 'pre_auth' : 'legacy';
	}

	/**
	 * Instant, en millisecondes epoch, à partir duquel la modale de sélection de base ne
	 * doit plus aboutir : le ticket de pré-authentification serait expiré.
	 *
	 * À appeler immédiatement au retour de `WorkspaceList`, la durée renvoyée étant
	 * relative à cet instant.
	 *
	 * @param {Partial<import('@knitiv/api-client-javascript').WorkspaceListOutput> | undefined} session
	 * 	 La réponse de `WorkspaceList`
	 * @returns {number | undefined} L'échéance, ou `undefined` si non applicable
	 */
	static preAuthCloseAt(session) {
		if (LoginCtrl.authMode(session) !== 'pre_auth') {
			return undefined;
		}

		const ttl = session?.auth_pending_expires_in;
		if (typeof ttl !== 'number' || ttl <= 0) {
			return undefined;
		}

		return Date.now() + (ttl * 1000) - PRE_AUTH_CLOSE_MARGIN_MS;
	}

	/* @ngInject */
	/**
	 * @param {import('angular').ILocationService} $location
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').route.IRouteParamsService} $routeParams
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {import('angular').IWindowService} $window
	 * @param {any} fromMail
	 * @param {any} Notification
	 * @param {any} User
	 */
	constructor(
		$location,
		$rootScope,
		$routeParams,
		$timeout,
		$translate,
		$uibModal,
		$window,
		fromMail,
		Notification,
		User,
	) {
		this.$location = $location;
		this.$rootScope = $rootScope;
		this.$routeParams = $routeParams;
		this.$translate = $translate;
		this.$timeout = $timeout;
		this.$location = $location;
		this.$uibModal = $uibModal;
		this.$window = $window;
		this.fromMail = fromMail;
		this.Notification = Notification;
		this.User = User;

		this.username = undefined;
		this.password = undefined;
		this.jwt = undefined;
		this.showLogin = false;
		this.showLoginLink = false;
		this.showPass = false;
		this.loading = true;
		this.loginLogo = '';
		this.logging = false;
		this.host = $window.location.hostname;

		/** @type {Knitiv.GetConfigOutput['auth_method']} */
		this.authMethods = [];

		if ($rootScope.sso === undefined) {
			$rootScope.sso = false;
		}

		/* Theme */
		/** @type {Knitiv.AppConfigTheme | undefined} */
		const theme = this.$rootScope.appConfig?.theme || undefined;
		this.loginLogo = theme?.logo ?? logo;
		this.buttonStyle = {
			background: theme?.form?.submitButtonColor ?? undefined,
		};
		this.formStyle = {
			background: theme?.form?.background ?? undefined,
		};

		// example /login/_K1_LCB0EDC9D9B1A1C5D5F1EC8949A1254D1DF4DCD8D4ECECE4F051691DD001D81545655525
		if ($routeParams.redirectTo) {
			$rootScope.redirectTo = $routeParams.redirectTo;
		}

		if ($routeParams.redirect) {
			fromMail.push(Utils.k1Decode($routeParams.redirect));
		}

		if ($routeParams.jwt) {
			this.username = undefined;
			this.password = undefined;
			this.jwt = $routeParams.jwt;
			this.logging = true;
			(async() => this.login(true))();
		}
		else if ($routeParams.error && $routeParams.error_description) {
			let message = `<b>${$routeParams.error}</b>&nbsp;: ${$routeParams.error_description}`;
			if ($routeParams.error_uri) {
				message += `<br><a href="${$routeParams.error_uri}">Plus de détails</a>`;
			}
			if ($routeParams.state) {
				message += `<br>${$routeParams.state}`;
			}
			if ($routeParams.file && $routeParams.line) {
				message += `<br><samp>${$routeParams.file}:${$routeParams.line}</samp>`;
			}
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message,
			});
		}

		// on redirige l'user vers la page si il y un redirect.
		if (fromMail.is()) {
			(async () => {
				const redirect = await fromMail.pop();
				$location.url(redirect);
			})();
		}

		$rootScope.userPassChange = false;

		if ('logout' in $location.search() && $rootScope.sso) {
			$location.search({ logout: null })
				.replace();
			this.loading = false;
		}

		(async () => {
			await this.User.fetchBackConfig();
			this.$timeout(() => {
				this.authMethods = this.$rootScope.appConfig.auth_method;
				this.showLogin = this.$rootScope.appConfig.showLogin ?? true;
				this.showLoginLink = this.$rootScope.appConfig.showLoginLink ?? false;

				if (this.authMethods.length === 0) {
					this.showLogin = true;
				}
			});
		})();

		this.loading = false;
	}

	/**
	 * @param {any} auth
	 */
	auth(auth) {
		if (auth?.path) {
			this.$window.location.replace(`/${auth.path}`);
		}
		else if (auth?.url) {
			this.$window.location.replace(auth.url);
		}
	}

	/**
	 * Ouvre le formulaire de support (front5), en pré-remplissant l'URL d'origine.
	 * Depuis le login, l'utilisateur n'est pas connecté : on ne transmet que l'URL.
	 */
	contactSupport() {
		this.$window.location.href = buildV5Url(this.$rootScope?.appConfig?.config?.V5?.url, 'support-request', {
			url: this.$window.location.href,
		});
	}

	async forgetpass() {
		if (this.username?.trim() === '' || !this.username) {
			// Ce lien est le point d'entrée des premières connexions : quelqu'un qui arrive
			// de son email d'invitation n'a encore rien saisi. On l'oriente vers le champ
			// identifiant plutôt que de lui opposer une erreur.
			this.Notification.info({
				title:   this.$translate.instant('common.info'),
				message: this.$translate.instant('error.missing-login'),
			});
			/** @type {?HTMLElement} */
			const usernameInput = document.querySelector('#username');
			usernameInput?.focus();
			return;
		}

		try {
			await this.$uibModal.open({
				animation:       true,
				ariaLabelledBy:  'modal-confirm-password-title',
				ariaDescribedBy: 'modal-confirm-password-body',
				template:        templateModalLoginConfirmResetPassword,
				controller:      ModalLoginConfirmResetPasswordController,
				controllerAs:    '$ctrl',
				resolve:         {
					username: () => this.username,
				},
			}).result;

			const output = await APIV5.instance.passForgotten({ username: this.username }, { id: 'login.forgetpass' });
			if (output?.error === null) {
				this.$uibModal.open({
					animation:       true,
					ariaLabelledBy:  'modal-password-reset-mail-title',
					ariaDescribedBy: 'modal-password-reset-mail-body',
					template:        templateModalLoginPasswordResetMail,
					controller:      ModalLoginPasswordResetMailController,
					controllerAs:    '$ctrl',
					resolve:         {
						username:        () => this.username,
						delayExpiration: () => output.delay_expiration,
						delayUnit:       () => output.delay_unit,
					},
				});
			}
			else {
				this.Notification.error({
					title:   this.$translate.instant('error.login-invalid'),
					message: this.$translate.instant('error.contact-admin'),
				});
				angular.element('#username')
					.trigger('focus');
			}
		}
		catch (error) {
			if (error !== 'cancel') {
				console.error(error);
			}
		}
	}

	/**
	 * @param {boolean} [auto]
	 * @returns {Promise<any>} Promise result
	 */
	async login(auto = false) {
		// event.preventDefault();
		if (!auto && (!this.username || !this.password)) {
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message: this.$translate.instant(`error.${this.username ? 'password-empty' : 'login-empty'}`),
			});
		}
		this.showPass = false;
		this.loading = true;

		try {
			const result = await APIV5.instance.workspaceList(this.jwt ? {
				jwt: this.jwt,
			} : {
				username: this.username,
				password: this.password,
			}, { id: 'login.login' });
			if (result) {
				// Calculé ici, au plus près de la réponse : la durée renvoyée est relative
				// à cet instant.
				const closeAt = LoginCtrl.preAuthCloseAt(result);

				if (LoginCtrl.authMode(result) === 'pre_auth') {
					// `WorkspaceList` a posé le cookie de pré-authentification : le mot de passe
					// n'a plus aucun usage et ne doit pas survivre à la sélection de la base.
					// Voir docs/auth-contract-v2.md §3.
					this.password = undefined;

					// On sort d'un `await` : cette affectation a lieu hors du cycle de digest.
					// Le modèle est vidé immédiatement, mais sans ce déclenchement explicite
					// l'`<input>` conserverait sa valeur à l'écran jusqu'au prochain digest.
					this.$timeout();
				}

				const session = {
					...result,
					username: this.username,
					password: this.password,
				};

				if (
					!session.workspaces
					|| session.workspaces.info.count_env === 0
					|| session.workspaces.info.total_count_database === 0
				) {
					this.loading = false;

					if ((!auto && (this.username || this.password)) || session.auth === 'Negotiate') {
						this.Notification.error({
							title:   this.$translate.instant('error.title'),
							message: this.$translate.instant('error.no-database'),
						});
					}
					else {
						const search = this.$location.search();

						if (search.logout) {
							search.logout = null;
						}

						if (search.error) {
							search.error = null;
						}

						this.$location.search(search);
					}
				}
				else if (session.workspaces.info.total_count_database === 1) {
					const environmentList = session.workspaces.env_list
					const sessionEnvironmentKid = session.env_kid
					if (sessionEnvironmentKid in environmentList) {
						// @ts-ignore
						const environment = environmentList[sessionEnvironmentKid]
						if (environment) {
							const databaselist = Array.isArray(environment.database_list)
								? environment.database_list
								: Object.values(environment.database_list);

							this.logging = true;
							// eslint-disable-next-line promise/catch-or-return,promise/no-nesting
							await this.userSet(session, {
								env:      sessionEnvironmentKid,
								database: databaselist[0],
								// eslint-disable-next-line promise/always-return
							});
						}
					}
				}
				else {
					this.logging = true;
					const modalInstance = this.$uibModal.open({
						animation:       true,
						ariaLabelledBy:  'modal-login-workspaces-title',
						ariaDescribedBy: 'modal-login-workspaces-body',
						template:        templateModalLoginWorkspaces,
						controller:      ModalLoginWorkspacesController,
						controllerAs:    '$ctrl',
						resolve:         {
							admin:      () => result.profil_admin === 1,
							urls:       () => session.urls,
							workspaces: () => session.workspaces,
						},
					});

					// Fermeture proactive à l'approche de l'expiration du ticket. Les
					// navigateurs bridant fortement les minuteurs dans les onglets en
					// arrière-plan — précisément le cas de l'utilisateur qui s'absente —
					// elle ne se déclenchera pas de façon fiable : d'où le contrôle du
					// temps écoulé juste après, évalué au moment de l'action.
					const expiryTimer = closeAt === undefined ? undefined : this.$timeout(
						() => modalInstance.dismiss(PRE_AUTH_EXPIRED),
						Math.max(closeAt - Date.now(), 0),
					);

					/** @type {any} */
					let workspace;
					try {
						workspace = await modalInstance.result;
					}
					finally {
						if (expiryTimer) {
							this.$timeout.cancel(expiryTimer);
						}
					}

					if (closeAt !== undefined && Date.now() >= closeAt) {
						throw PRE_AUTH_EXPIRED;
					}

					this.$rootScope.title = '';
					await this.userSet(session, workspace);
					// eslint-disable-next-line promise/always-return
					if (this.fromMail.is()) {
						// eslint-disable-next-line promise/catch-or-return,promise/no-nesting
						const redirect = await this.fromMail.pop();
						this.$location.url(redirect);
					}
				}
			}
			else {
				this.loading = false;
				this.logging = false;
			}
		}
		catch (error) {
			this.loading = false;
			this.logging = false;
			this.$rootScope.title = '';

			// Le mot de passe ayant été effacé au retour de `WorkspaceList`, le formulaire
			// est déjà vide : l'utilisateur n'a qu'à le ressaisir.
			if (error === PRE_AUTH_EXPIRED) {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.auth-expired'),
				});
				return;
			}

			console.error('login error', error);
			if (error !== 'cancel' && error !== 'backdrop click') {
				console.error('modalInstance.result error', error);
			}
		}
	}

	loginSSO() {
		this.username = undefined;
		this.password = undefined;
	}

	/**
	 * @param {any} user
	 * @param {any} workspace
	 */
	async userSet(user, workspace) {
		const authMode = LoginCtrl.authMode(user);

		this.username = user.username;
		if (authMode === 'legacy') {
			this.password = user.password;
		}
		this.loading = true;

		// Le mode SSO se déduit du mécanisme d'authentification renvoyé par `WorkspaceList`
		// (`auth`), et non plus de l'absence d'identifiants : en mode `pre_auth` le mot de
		// passe est effacé pour toutes les connexions, y compris classiques, ce qui rendrait
		// l'ancienne heuristique systématiquement vraie.
		// Repli sur l'absence de `username` si le back ne renvoie pas `auth` — le cas reste
		// à confirmer pour la branche JWT. Voir docs/auth-contract-v2.md §7.
		const isSSO = user.auth ? user.auth !== 'K' : !user.username;
		if (isSSO) {
			this.$rootScope.sso = true;
		}

		// Remove notification messages
		if (this.$rootScope.scopeNotification) {
			this.$rootScope.scopeNotification.kill();
		}

		/** @type {import('@knitiv/api-client-javascript').TimezoneLogin} */
		const tz = {
			name:   dayjs.tz.guess(),
			offset: dayjs().utcOffset(),
		};

		try {
			const base = {
				// `clientid` identifie le terminal, pas l'utilisateur : le back le stocke en
				// `terminal_id` et le journalise. Il reste donc requis dans les deux modes.
				clientid: user._know_,
				dsnasso:  workspace.database.dsnasso_kid,
				terminalid,
				tz,
				...(workspace.date ? { date: { target: workspace.date } } : {}),
			};

			// `LoginInput` est une union : chaque forme d'identification est un objet distinct,
			// à construire d'un seul tenant. En mode `pre_auth`, l'identité est portée par le
			// cookie posé par `WorkspaceList` et aucun identifiant ne transite ici.
			/** @type {import('@knitiv/api-client-javascript').LoginInput} */
			const loginRequest = authMode === 'pre_auth' ? base : {
				...base,
				username: user.username,
				password: user.password,
				...(this.jwt ? { jwt: this.jwt } : {}),
			};

			/** @type {any} */
			const loginResponse = await APIV5.instance.login(loginRequest, { id: 'login.user-set' });

			if (loginResponse) {
				setAPI(loginResponse, this.$location.url(), this.$rootScope.tag);
				this.User.set(loginResponse)
					.saveToken();

				this.User.fetchBackConfig();
				this.$timeout(() => {
					if (loginResponse.passchange === 1) {
						this.$rootScope.userPassChange = true;
						this.$location.path('/passchange');
					}
					else {
						this.$rootScope.$emit('User.login');
						this.$rootScope.goToPreviousURL();
					}
					this.loading = false;
				});
			}
			else {
				// L'échec est notifié par l'emitter d'`api.js` à partir de `errno_desc`.
				// On rend la main au formulaire : sans réinitialiser `logging`, l'écran
				// resterait bloqué en « connexion en cours ». En mode `pre_auth` le mot de
				// passe a déjà été effacé, l'utilisateur devra le ressaisir — c'est le
				// comportement attendu, notamment si le jeton de pré-authentification a
				// expiré pendant la sélection de la base.
				this.$timeout(() => {
					this.loading = false;
					this.logging = false;
				});
			}
		}
		catch (error) {
			this.loading = false;
			this.logging = false;
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message: Utils.encodeHTMLEntities(error?.toString()),
			});
		}
	}
}
